import React, { useEffect, useState, useMemo } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import Icon from "../../components/Icon";

// i2-visual-identity-v2.md §10.1 A12 — Marketplace ⌘K palette.
//
// Mirrors features/library/iphone-grid/LibraryCommandPalette but the
// content shape is template-flavored:
//   - Recent searches (capped 5, persisted in marketplace.recent_searches)
//   - Quick actions: Browse Featured / Publish / Open Library
//   - Matching templates → click → onSelectTemplate(t)
//
// Triggered by:
//   1. Cmd+K (or Ctrl+K on Windows/Linux) global hotkey while on /marketplace
//   2. Click on the slim "Search · ⌘K" pill (future toolbar trigger)
//
// Selection commits via Click / Enter; Esc dismisses.

const _VISUALLY_HIDDEN = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap",
  borderWidth: 0,
};

const RECENT_KEY = "marketplace.recent_searches";

export function readRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw).slice(0, 5) : [];
  } catch { return []; }
}

export function pushRecentSearch(q) {
  if (!q || !q.trim()) return;
  try {
    const list = readRecentSearches();
    const next = [q, ...list.filter(x => x !== q)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

let _cmdkStylesMounted = false;
export function ensureCmdkStyles() {
  if (typeof document === "undefined" || _cmdkStylesMounted) return;
  if (document.querySelector("style[data-cmdk-marketplace]")) {
    _cmdkStylesMounted = true; return;
  }
  _cmdkStylesMounted = true;
  const style = document.createElement("style");
  style.setAttribute("data-cmdk-marketplace", "");
  style.textContent = `
    [data-testid="marketplace-cmdk-root"] [cmdk-list] [cmdk-item][data-selected="true"] {
      background: var(--cmdk-active-bg, rgba(124, 90, 245, 0.18));
    }
    [data-testid="marketplace-cmdk-root"] [cmdk-list] [cmdk-group-heading] {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      padding: 8px 10px 4px;
      opacity: 0.6;
    }
  `;
  document.head.appendChild(style);
}

export default function MarketplaceCommandPalette({
  open, onOpenChange, templates = [],
  onSelectTemplate,
  onPublish,
  onBrowseFeatured,
  onOpenLibrary,
}) {
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(() => readRecentSearches());

  useEffect(() => { ensureCmdkStyles(); }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setRecents(readRecentSearches());
    }
  }, [open]);

  // Persist search to recents when user actually narrows results.
  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(() => pushRecentSearch(query), 600);
    return () => clearTimeout(t);
  }, [query]);

  const sortedTemplates = useMemo(() => {
    return [...(templates || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [templates]);

  if (!open) return null;

  const handleSelectTemplate = (t) => {
    pushRecentSearch(t.name || "");
    onSelectTemplate && onSelectTemplate(t);
    onOpenChange(false);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Search marketplace templates"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(2px)",
        animation: "fadeIn 0.15s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <Dialog.Title style={_VISUALLY_HIDDEN}>Search marketplace templates</Dialog.Title>
      <Dialog.Description style={_VISUALLY_HIDDEN}>
        Type to filter templates, run a quick action, or open a recent search.
        Press Escape to close.
      </Dialog.Description>
      <div
        data-testid="marketplace-cmdk-root"
        onClick={(e) => e.stopPropagation()}
        style={{
          marginTop: "10vh",
          width: "min(640px, 92vw)",
          maxHeight: "70vh",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: RADIUS.deep,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          ...(MATERIALS?.thickGlass ? MATERIALS.thickGlass() : {}),
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`,
        }}>
          <Icon name="action.search" size={16} color={COLORS.textDim} />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Search templates — name, description, slug…"
            autoFocus
            style={{
              flex: 1, fontSize: 15,
              color: COLORS.text, background: "transparent",
              border: "none", outline: "none",
            }}
          />
          <kbd style={{
            padding: "2px 6px", fontSize: 10, fontWeight: 600,
            color: COLORS.textDim,
            background: `${COLORS.border}40`,
            border: `1px solid ${COLORS.border}`, borderRadius: 4,
          }}>esc</kbd>
        </div>

        <Command.List style={{ overflowY: "auto", padding: 6 }}>
          <Command.Empty style={{
            padding: "24px 16px", textAlign: "center",
            fontSize: 13, color: COLORS.textMuted,
          }}>
            No templates match {query ? `"${query}"` : "your search"}.
          </Command.Empty>

          {/* Recent searches — only when query is empty */}
          {!query && recents.length > 0 && (
            <Command.Group heading="Recent searches">
              {recents.map((r, i) => (
                <Command.Item
                  key={`recent-${i}`}
                  data-testid={`marketplace-cmdk-recent-${i}`}
                  value={`recent-${r}`}
                  onSelect={() => { setQuery(r); }}
                  style={cmdkItemStyle()}
                >
                  <Icon name="action.search" size={12} color={COLORS.textDim} />
                  <span style={{ marginLeft: 8 }}>{r}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Quick actions */}
          {!query && (
            <Command.Group heading="Quick actions">
              <Command.Item
                data-testid="marketplace-cmdk-action-browse-featured"
                value="action-browse-featured"
                onSelect={() => {
                  onBrowseFeatured && onBrowseFeatured();
                  onOpenChange(false);
                }}
                style={cmdkItemStyle()}
              >
                <Icon name="action.star" size={12} color={COLORS.orange} />
                <span style={{ marginLeft: 8 }}>Browse Featured</span>
              </Command.Item>
              <Command.Item
                data-testid="marketplace-cmdk-action-publish"
                value="action-publish"
                onSelect={() => {
                  onPublish && onPublish();
                  onOpenChange(false);
                }}
                style={cmdkItemStyle()}
              >
                <Icon name="action.upload" size={12} color={COLORS.accent} />
                <span style={{ marginLeft: 8 }}>Publish a template…</span>
              </Command.Item>
              <Command.Item
                data-testid="marketplace-cmdk-action-open-library"
                value="action-open-library"
                onSelect={() => {
                  onOpenLibrary && onOpenLibrary();
                  onOpenChange(false);
                }}
                style={cmdkItemStyle()}
              >
                <Icon name="tab.library" size={12} color={COLORS.cyan} />
                <span style={{ marginLeft: 8 }}>Go to Agent Library</span>
              </Command.Item>
            </Command.Group>
          )}

          {/* Templates */}
          {(query || recents.length === 0) && (
            <Command.Group heading={query ? "Matches" : "Templates"}>
              {sortedTemplates.map(t => (
                <Command.Item
                  key={t.id}
                  data-testid={`marketplace-cmdk-result-${t.id}`}
                  value={`tpl-${(t.name || "")} ${(t.description || "")} ${t.slug || ""}`}
                  onSelect={() => handleSelectTemplate(t)}
                  style={cmdkItemStyle()}
                >
                  <span style={{
                    fontSize: 13, fontWeight: 600, color: COLORS.text,
                  }}>{t.name}</span>
                  {t.author_name && (
                    <span style={{
                      fontSize: 10, color: COLORS.textDim, marginLeft: 8,
                    }}>by {t.author_name}</span>
                  )}
                  {t.avg_rating ? (
                    <span style={{
                      fontSize: 10, color: COLORS.orange, marginLeft: "auto",
                    }}>★ {Number(t.avg_rating).toFixed(1)}</span>
                  ) : null}
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>

        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "8px 14px",
          borderTop: `1px solid ${COLORS.border}`,
          background: `${COLORS.border}10`,
          fontSize: 11, color: COLORS.textMuted,
        }}>
          <span><KbdHint>↑↓</KbdHint> Navigate</span>
          <span><KbdHint>↵</KbdHint> Open</span>
          <span><KbdHint>esc</KbdHint> Dismiss</span>
        </div>
      </div>
    </Command.Dialog>
  );
}

function cmdkItemStyle() {
  return {
    display: "flex", alignItems: "center", gap: 4,
    padding: "8px 12px", borderRadius: 6,
    cursor: "pointer", fontSize: 13,
  };
}

function KbdHint({ children }) {
  return (
    <kbd style={{
      padding: "1px 5px", fontSize: 10, fontWeight: 600,
      color: COLORS.textDim, background: `${COLORS.border}40`,
      border: `1px solid ${COLORS.border}`, borderRadius: 3,
    }}>{children}</kbd>
  );
}
